import { Container, Heading, Box, Flex } from '@chakra-ui/layout';
import React from 'react';
import { SkillSvg } from '../components/skillSVG';
import {
  useColorMode,
  Link,
  Image,
  Tooltip,
  Grid,
  Spinner,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { frontEnd, backEnd, utilities } from '../utils/skillsIcons';
import Reveal from './../components/Reveal';
import Mario from '../components/Mario';
import catGif from '../utils/cat.gif';

const MotionContainer = motion(Container);

const Skills = ({ focused }) => {
  const { colorMode } = useColorMode();

  return (
    <MotionContainer
      maxW={{
        base: 'container.sm',
        md: 'container.md',
        lg: 'container.lg',
        xl: 'container.xl',
      }}
      transition="linear .1s"
      pb={8}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="skills-svg"
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '30px',
          marginBottom: '20px',
          position: 'relative',
        }}
      >
        <SkillSvg />
      </motion.div>
      <Box
        className="skills-content"
        pt={6}
        pb={6}
        position="relative"
        transition="linear 0.1s"
        borderRadius="2xl"
        border="4px"
        borderColor={focused ? 'blue.200' : 'transparent'}
        backgroundColor={colorMode === 'light' ? 'white' : '#1A202C'}
        overflow="hidden"
      >
        <Box textAlign="center" mt={12} mb={12}>
          <Heading as="h1" size="2xl">
            My Skills & Tools
          </Heading>
        </Box>
        <Heading as="h1" lineHeight={1.4} mr={6} ml={6} textAlign="center">
          🌈 Front-End Skillset
        </Heading>
        <Grid
          templateColumns={{ base: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }}
          justifyItems="center"
          mt={8}
          mb={8}
        >
          {frontEnd.map(({ title, image }) => {
            return (
              <Reveal key={title}>
                <Tooltip
                  label={title}
                  fontSize="md"
                  placement="right-start"
                  hasArrow
                >
                  <Box
                    p={4}
                    transition="linear .1s"
                    border="4px"
                    borderColor="transparent"
                    borderRadius="2xl"
                    _hover={{
                      borderColor: 'blue.200',
                      backgroundColor: 'rgba(0,0,0,0.5)',
                    }}
                  >
                    <Image
                      src={image}
                      boxSize="100"
                      alt={title}
                      objectFit="contain"
                      fallback={<Spinner size="xl" />}
                    />
                  </Box>
                </Tooltip>
              </Reveal>
            );
          })}
        </Grid>
        <Reveal>
          <Heading as="p" size="md" lineHeight={1.4} mr={6} ml={6}>
            For Front-End Web Development, I'm currently comfortable in using{' '}
            <Link
              color="orange.500"
              href="https://www.w3schools.com/html/"
              isExternal
            >
              HTML5
            </Link>
            ,{' '}
            <Link
              color="orange.500"
              href="https://www.w3schools.com/css/"
              isExternal
            >
              CSS3
            </Link>
            ,{' '}
            <Link
              color="orange.500"
              href="https://developer.mozilla.org/en-US/docs/Web/JavaScript"
              isExternal
            >
              Javascript
            </Link>
            , and{' '}
            <Link color="orange.500" href="https://reactjs.org/" isExternal>
              ReactJS
            </Link>
            . But, I always utilize UI frameworks such as{' '}
            <Link color="orange.500" href="https://chakra-ui.com/" isExternal>
              ChakraUI
            </Link>
            ,{' '}
            <Link color="orange.500" href="https://tailwindcss.com/" isExternal>
              TailwindCSS
            </Link>
            {' and '}
            <Link
              color="orange.500"
              href="https://getbootstrap.com/"
              isExternal
            >
              Bootstrap
            </Link>{' '}
            whenever I make projects to speed up the process. (Let's be honest
            here, no one wants to spend an hour just to center a div inside
            another div lol). And I also comfortable with using{' '}
            <Link color="orange.500" href="https://reactnative.dev/" isExternal>
              React-Native
            </Link>{' '}
            for Mobile App Dev. I love to create an interactive App with some
            fancy animations, it looks cool! I mean just look at this Mario
            flipping around, tell me that's not cool 😂.
          </Heading>
        </Reveal>
        <Mario />
        <Heading as="h1" lineHeight={1.4} mr={6} ml={6} textAlign="center">
          Back-End Battalion 😤
        </Heading>
        <Grid
          templateColumns={{ base: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }}
          justifyItems="center"
          mt={8}
          mb={8}
        >
          {backEnd.map(({ title, image }) => {
            return (
              <Reveal key={title}>
                <Tooltip
                  label={title}
                  fontSize="md"
                  placement="right-start"
                  hasArrow
                >
                  <Box
                    p={4}
                    transition="linear .1s"
                    border="4px"
                    borderColor="transparent"
                    borderRadius="2xl"
                    _hover={{
                      borderColor: 'blue.200',
                      backgroundColor: 'rgba(0,0,0,0.5)',
                    }}
                  >
                    <Image
                      src={image}
                      boxSize="100"
                      alt={title}
                      objectFit="contain"
                      fallback={<Spinner size="xl" />}
                    />
                  </Box>
                </Tooltip>
              </Reveal>
            );
          })}
        </Grid>
        <Reveal>
          <Heading as="p" size="md" lineHeight={1.4} mr={6} ml={6}>
            For Back-End Development, I'm comfortable using{' '}
            <Link color="orange.500" href="https://nodejs.org/en/" isExternal>
              NodeJS
            </Link>
            ,{' '}
            <Link color="orange.500" href="https://expressjs.com/" isExternal>
              ExpressJS
            </Link>
            ,{' '}
            <Link color="orange.500" href="https://www.mongodb.com/" isExternal>
              MongoDB
            </Link>
            ,{' and '}
            <Link
              color="orange.500"
              href="https://firebase.google.com/"
              isExternal
            >
              Firebase
            </Link>
            . I utilize{' '}
            <Link color="orange.500" href="https://www.postman.com/" isExternal>
              Postman
            </Link>{' '}
            so I can easily test my API endpoints without the hassle of building
            the client App. I also have an experience using relational database
            such as{' '}
            <Link color="orange.500" href="https://www.mysql.com/" isExternal>
              MySQL
            </Link>{' '}
            but I'm really rusty on relational databases and might need some
            time to refresh my knowledge 😅.
          </Heading>
        </Reveal>
        <Reveal>
          <Heading
            as="h1"
            lineHeight={1.4}
            mr={6}
            ml={6}
            mt={12}
            textAlign="center"
          >
            Other tools that I use
          </Heading>
          <Grid
            templateColumns={{ base: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }}
            justifyItems="center"
            mt={8}
            mb={8}
          >
            {utilities.map(({ title, image }) => {
              return (
                <Reveal key={title}>
                  <Tooltip
                    label={title}
                    fontSize="md"
                    placement="right-start"
                    hasArrow
                  >
                    <Box
                      p={4}
                      transition="linear .1s"
                      border="4px"
                      borderColor="transparent"
                      borderRadius="2xl"
                      _hover={{
                        borderColor: 'blue.200',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                      }}
                    >
                      <Image
                        src={image}
                        boxSize="100"
                        alt={title}
                        objectFit="contain"
                        fallback={<Spinner size="xl" />}
                      />
                    </Box>
                  </Tooltip>
                </Reveal>
              );
            })}
          </Grid>
          <Heading as="p" size="md" lineHeight={1.4} mr={6} ml={6}>
            Yep, that pretty much sums up my skills and the tools that I use, at
            least for now. I'm excited to see what more technology can I add to
            this page in the upcoming years!
          </Heading>
          <Reveal>
            <Flex justifyContent="center" alignItems="center" mt={8}>
              <Image
                src={catGif}
                objectFit="contain"
                width={{ base: '125px', md: '150px' }}
                borderRadius="2xl"
                fallback={<Spinner size="xl" />}
              />
            </Flex>
          </Reveal>
        </Reveal>
      </Box>
    </MotionContainer>
  );
};

export default Skills;
